/**
 * The seasonal stretches of a map's lines, each drawn once. Two seasonal lines sailing the same lane (Fjord Line's direct
 * Hirtshals–Bergen and its call at Kristiansand) are drawn on top of each other, their dashes starting from their own
 * first point: out of step, each fills the other's gaps and the lane reads solid. So the map draws its seasonal lines
 * from these lanes instead: each stretch once, under the all-year lines, which draw a stretch they sail too solid. Only
 * the drawing: a hover (orange, solid) and a click still read each line.
 */
import { getOrSet } from '../lib/maps';
import type { MapPage } from './page';

/** A line of the map's data: its route's id, whether every one of its sailings is seasonal, and its points. */
export interface LaneInput {
  properties: { id?: number; seasonal?: boolean };
  geometry: { type: 'LineString'; coordinates: number[][] };
}

/** A stretch of seasonal lane, as long as it is drawn once. */
type Lane = { type: 'Feature'; properties: Record<string, never>; geometry: { type: 'LineString'; coordinates: number[][] } };

export interface Lanes {
  type: 'FeatureCollection';
  features: Lane[];
}

/**
 * How close, in degrees (a kilometre or so), two lines' points at sea are one point: lanes drawn by hand that far apart
 * overlap on the map.
 */
const SAME_POINT = 0.015;
/** Whether two points are one: no more than `SAME_POINT` apart along each axis. */
const near = (p: number[], q: number[]) => Math.abs(p[0] - q[0]) <= SAME_POINT && Math.abs(p[1] - q[1]) <= SAME_POINT;

/** A grid cell's key: its column times this, plus its row (a row is never as far as this from the equator). */
const ROW = 1e6;

/** The grid cells around a point, its own among them, as numbers: string keys made the cut four times slower. */
function around(x: number, y: number): number[] {
  const [cx, cy] = [Math.floor(x / SAME_POINT), Math.floor(y / SAME_POINT)];
  return [-1, 0, 1].flatMap((dx) => [-1, 0, 1].map((dy) => (cx + dx) * ROW + cy + dy));
}
/** The grid cell of a point. */
const cellOf = (x: number, y: number) => Math.floor(x / SAME_POINT) * ROW + Math.floor(y / SAME_POINT);

/**
 * Each seasonal line's points, a point at sea within `SAME_POINT` of a point of other lines moved onto it, so two lines
 * drawn along one lane share their points; found by a grid of cells as wide, among the cells around. A port (`ports`,
 * the ends and stops) stays where it is and draws no line it is not on, and a point near nothing but its own line's stays (a line's own being every
 * point it has, ports it shares with others included). The same coordinates are one point, so points are compared as
 * objects from here on.
 */
function snapped(lines: LaneInput[], ports: Set<string>): number[][][] {
  const grid = new Map<number, number[][]>();
  const exact = new Map<string, number[]>();
  const owners = new Map<number[], Set<number>>();
  return lines.map((line, n) =>
    line.geometry.coordinates.map(([x, y]) => {
      let point = exact.get(`${x},${y}`);
      if (!point && !ports.has(`${x},${y}`))
        point = around(x, y)
          .flatMap((cell) => grid.get(cell) ?? [])
          .find((seen) => !owners.get(seen)!.has(n) && !ports.has(seen.join()) && near(seen, [x, y]));
      if (!point) {
        point = [x, y];
        exact.set(`${x},${y}`, point);
        const cell = cellOf(x, y);
        getOrSet(grid, cell, () => []).push(point);
        owners.set(point, new Set());
      }
      owners.get(point)!.add(n);
      return point;
    }),
  );
}

/**
 * Each line's points with, on each stretch, the points lying on it (within `SAME_POINT`, another line's or its own when
 * it doubles back) put in sailing order, so a line sailing straight past where another turns is cut where it is, and
 * their stretches match. Found by the grid cells along the stretch.
 */
function cut(shapes: number[][][], ports: Set<string>): number[][][] {
  const grid = new Map<number, Set<number[]>>();
  for (const point of shapes.flat()) {
    const cell = cellOf(point[0], point[1]);
    getOrSet(grid, cell, () => new Set()).add(point);
  }
  return shapes.map((points) => {
    const own = new Set(points);
    return points.flatMap((b, i) => {
      if (i === 0) return [b];
      const a = points[i - 1];
      const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
      const length = Math.hypot(dx, dy);
      if (length === 0) return [b];
      const on = new Map<number[], number>();
      for (let step = 0; step <= length + SAME_POINT; step += SAME_POINT) {
        const along = Math.min(step, length) / length;
        const cell = cellOf(a[0] + dx * along, a[1] + dy * along);
        // The nine cells around, as `around` gives them but by arithmetic: this loop runs a few hundred thousand times on
        // a big map
        for (let k = 0; k < 9; k++)
          for (const seen of grid.get(cell + (Math.floor(k / 3) - 1) * ROW + (k % 3) - 1) ?? []) {
            // A port only on a line that calls there: a line passing by it does not
            if (seen === a || seen === b || on.has(seen) || (ports.has(seen.join()) && !own.has(seen))) continue;
            // Its own bend just before or after the stretch is no doubling back: taken, it would cut the corner it turns
            if ((seen === points[i - 2] && near(seen, a)) || (seen === points[i + 1] && near(seen, b))) continue;
            // Where along the stretch it lies, and how far off it
            const t = ((seen[0] - a[0]) * dx + (seen[1] - a[1]) * dy) / (length * length);
            const off = Math.abs((seen[0] - a[0]) * dy - (seen[1] - a[1]) * dx) / length;
            if (t > 0 && t < 1 && off <= SAME_POINT) on.set(seen, t);
          }
      }
      return [...[...on].sort((x, y) => x[1] - y[1]).map(([point]) => point), b];
    });
  });
}

/** A stretch between two points, whichever way it is sailed. */
const stretch = (a: number[], b: number[]) => [a.join(), b.join()].sort().join('|');

/** The lanes of the seasonal ones of `lines`, the ports (`ports`, their points as `x,y`) kept where they are. */
export function seasonalLanes(lines: LaneInput[], ports: Set<string>): Lanes {
  const shapes = cut(
    snapped(
      lines.filter((line) => line.properties.seasonal),
      ports,
    ),
    ports,
  );
  // Each line walked in order, its stretches not yet drawn chained, so the dashes run on along a lane; a point repeated
  // (or two moved onto one) adds nothing
  const drawn = new Set<string>();
  const features: Lane[] = [];
  for (const points of shapes) {
    let chain: Lane | null = null;
    for (let i = 1; i < points.length; i++) {
      if (points[i - 1] === points[i]) continue;
      const key = stretch(points[i - 1], points[i]);
      if (drawn.has(key)) {
        chain = null;
        continue;
      }
      drawn.add(key);
      if (chain) chain.geometry.coordinates.push(points[i]);
      else {
        chain = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [points[i - 1], points[i]] } };
        features.push(chain);
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

/** Each page's ports as points (`x,y`), once per page's data. */
const portSets = new WeakMap<MapPage, Set<string>>();

/**
 * A page's lanes: those of all its lines, or, on a map that draws its lines on hover only, of the lines of the routes
 * hovered (`hovered`), the home map's thousands of lines taking a phone a quarter of a second; none with none hovered.
 */
export function pageLanes(data: MapPage, hovered: number[] = []): Lanes {
  const lines = (data.routes.features as unknown as LaneInput[]).filter(
    (line) => !data.hoverLines || hovered.includes(line.properties.id!),
  );
  const ports = getOrSet(
    portSets,
    data,
    () => new Set(data.ports.features.flatMap((port) => (port.geometry.type === 'Point' ? [port.geometry.coordinates.join()] : []))),
  );
  return seasonalLanes(lines, ports);
}
